import { useLazyQuery } from "@apollo/client";
import { CloudFrameDefaultButton } from "@cloudframe/button";
import { CloudFrameDataTable } from "@cloudframe/data-table";
import {
  DetailsListLayoutMode,
  IColumn,
  IconButton,
  IIconProps,
  Pivot,
  PivotItem,
  SelectionMode,
} from "@fluentui/react";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  QUERY_GET_ACTIVATION_KEY_HISTORY,
  QUERY_GET_DEACTIVATION_KEY_HISTORY,
  QUERY_GET_MACHINE_HISTORY,
} from "../../graphql-query/continuum-scanner-license.query";
import { useStyles } from "./continuum-machine-activation-history.styles";

interface ContinuumMachineActivationHistoryProps {
  licenseId: string;
}

const backIcon: IIconProps = { iconName: "Back" };

const copyIcon: IIconProps = { iconName: "Copy" };

// Formats a stored timestamp to "YYYY-MM-DD HH:mm:ss" (drops noisy milliseconds)
// so it fits the date columns without truncation.
const formatDateTime = (value?: string): string => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};

// Plain truncated text with the full value on hover (native title). For short-ish
// columns (dates, status) that don't need a copy button.
const textCell = (value?: string) => (
  <span
    title={value ?? ""}
    style={{
      display: "block",
      width: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {value ?? ""}
  </span>
);

// Renders any value truncated to one line, with the full text available on hover
// (native title tooltip) and a copy button. Keeps rows short while nothing is lost.
const valueCell = (value?: string) => {
  const text = value ?? "";
  if (!text) return <span />;
  return (
    <div style={{ display: "flex", alignItems: "center", width: "100%", minWidth: 0 }}>
      <span
        title={text}
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </span>
      <IconButton
        iconProps={copyIcon}
        title="Copy"
        ariaLabel="Copy"
        styles={{ root: { flex: "0 0 auto", height: 28, width: 28 } }}
        onClick={() => navigator.clipboard.writeText(text)}
      />
    </div>
  );
};

const machineColumns: IColumn[] = [
  {
    key: "machineFingerprint",
    name: "Fingerprint",
    fieldName: "machineFingerprint",
    minWidth: 320,
    maxWidth: 560,
    isResizable: true,
    onRender: (item: any) => valueCell(item.machineFingerprint),
  },
  {
    key: "status",
    name: "Status",
    fieldName: "status",
    minWidth: 140,
    maxWidth: 200,
    isResizable: true,
    onRender: (item: any) => textCell(item.status),
  },
];

const activationKeyColumns: IColumn[] = [
  {
    key: "machineFingerprint",
    name: "Fingerprint",
    fieldName: "machineFingerprint",
    minWidth: 260,
    maxWidth: 360,
    isResizable: true,
    onRender: (item: any) => valueCell(item.machineFingerprint),
  },
  {
    key: "activationKey",
    name: "Key",
    fieldName: "activationKey",
    minWidth: 300,
    maxWidth: 460,
    isResizable: true,
    onRender: (item: any) => valueCell(item.activationKey),
  },
  {
    key: "generatedAt",
    name: "Generated",
    fieldName: "generatedAt",
    minWidth: 160,
    maxWidth: 180,
    isResizable: true,
    onRender: (item: any) => textCell(formatDateTime(item.generatedAt)),
  },
  {
    key: "expiryDate",
    name: "Expiry",
    fieldName: "expiryDate",
    minWidth: 160,
    maxWidth: 180,
    isResizable: true,
    onRender: (item: any) => textCell(formatDateTime(item.expiryDate)),
  },
  {
    key: "activatedByEmail",
    name: "Activated By",
    fieldName: "activatedByEmail",
    minWidth: 120,
    maxWidth: 160,
    isResizable: true,
    onRender: (item: any) => valueCell(item.activatedByEmail),
  },
];

const deactivationStatus = (item: any): string => {
  if (item.consumedAt) return "Consumed";
  if (new Date(item.expiresAt) < new Date()) return "Expired";
  return "Pending confirmation";
};

const deactivationKeyColumns: IColumn[] = [
  {
    key: "machineFingerprint",
    name: "Fingerprint",
    fieldName: "machineFingerprint",
    minWidth: 240,
    maxWidth: 340,
    isResizable: true,
    onRender: (item: any) => valueCell(item.machineFingerprint),
  },
  {
    key: "deactivationKey",
    name: "Key",
    fieldName: "deactivationKey",
    minWidth: 300,
    maxWidth: 460,
    isResizable: true,
    onRender: (item: any) => valueCell(item.deactivationKey),
  },
  {
    key: "issuedAt",
    name: "Issued",
    fieldName: "issuedAt",
    minWidth: 160,
    maxWidth: 180,
    isResizable: true,
    onRender: (item: any) => textCell(formatDateTime(item.issuedAt)),
  },
  {
    key: "expiresAt",
    name: "Expires",
    fieldName: "expiresAt",
    minWidth: 160,
    maxWidth: 180,
    isResizable: true,
    onRender: (item: any) => textCell(formatDateTime(item.expiresAt)),
  },
  {
    key: "status",
    name: "Status",
    minWidth: 150,
    maxWidth: 200,
    isResizable: true,
    onRender: (item: any) => textCell(deactivationStatus(item)),
  },
  {
    key: "requestedByEmail",
    name: "Requested By",
    fieldName: "requestedByEmail",
    minWidth: 120,
    maxWidth: 160,
    isResizable: true,
    onRender: (item: any) => valueCell(item.requestedByEmail),
  },
  {
    key: "deactivatedByEmail",
    name: "Deactivated By",
    fieldName: "deactivatedByEmail",
    minWidth: 120,
    maxWidth: 160,
    isResizable: true,
    onRender: (item: any) => valueCell(item.deactivatedByEmail),
  },
];

const ContinuumMachineActivationHistory = ({ licenseId }: ContinuumMachineActivationHistoryProps) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [loadMachineHistory, { data: machineData }] = useLazyQuery(QUERY_GET_MACHINE_HISTORY, {
    fetchPolicy: "network-only",
  });
  const [loadActivationHistory, { data: activationData }] = useLazyQuery(
    QUERY_GET_ACTIVATION_KEY_HISTORY,
    { fetchPolicy: "network-only" }
  );
  const [loadDeactivationHistory, { data: deactivationData }] = useLazyQuery(
    QUERY_GET_DEACTIVATION_KEY_HISTORY,
    { fetchPolicy: "network-only" }
  );

  useEffect(() => {
    loadMachineHistory({ variables: { licenseId } });
    loadActivationHistory({ variables: { licenseId } });
    loadDeactivationHistory({ variables: { licenseId } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseId]);

  const machines = machineData?.getMachineHistory || [];

  const fingerprintByMachineId = useMemo(() => {
    const map = new Map<number, string>();
    machines.forEach((m: any) => map.set(m.id, m.machineFingerprint));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineData]);

  const withFingerprint = (rows: any[]) =>
    rows.map((row) => ({
      ...row,
      machineFingerprint: fingerprintByMachineId.get(row.machineId) ?? "Unknown",
    }));

  return (
    <div className={classes.root}>
      <div className={classes.pivotCard}>
        <div className={classes.cardHeaderRow}>
          <CloudFrameDefaultButton
            text="Back to license key management"
            iconProps={backIcon}
            onClick={() => navigate(`/continuum-machine-activation/${licenseId}`)}
          />
          <span className={classes.licenseIdLabel}>License ID {licenseId}</span>
        </div>
        <Pivot className={classes.pivot} aria-label="Machine activation history">
          <PivotItem headerText="Machine History">
            <div className={classes.tableWrap}>
              <CloudFrameDataTable
                items={machineData?.getMachineHistory || []}
                columns={machineColumns}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.justified}
                onShouldVirtualize={() => false}
              />
            </div>
          </PivotItem>

          <PivotItem headerText="Activation Key History">
            <div className={classes.tableWrap}>
              <CloudFrameDataTable
                items={withFingerprint(activationData?.getActivationKeyHistory || [])}
                columns={activationKeyColumns}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.justified}
                onShouldVirtualize={() => false}
              />
            </div>
          </PivotItem>

          <PivotItem headerText="Deactivation Key History">
            <div className={classes.tableWrap}>
              <CloudFrameDataTable
                items={withFingerprint(deactivationData?.getDeactivationKeyHistory || [])}
                columns={deactivationKeyColumns}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.justified}
                onShouldVirtualize={() => false}
              />
            </div>
          </PivotItem>
        </Pivot>
      </div>
    </div>
  );
};

export default ContinuumMachineActivationHistory;
